import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAuth } from '@/contexts/AuthContext';
import { MyDataDocument } from '@/generated/graphql';
import { CampaignEntryContext } from './campaignEntryContext';
import { COMPLETE_MY_CAMPAIGN_ENTRY, MY_CAMPAIGN_ENTRY, type CampaignEntryState } from '@/queries/campaignEntry';

interface EntrySnapshot {
  session: object | null;
  loading: boolean;
  skipOnboarding: boolean;
  campaignId: string | null;
}

export function CampaignEntryProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient();
  const { user, isLoading, sessionVerified } = useAuth();
  const account = !isLoading && sessionVerified && user ? user.id : null;
  const session = useRef({ account, identity: {} });
  if (session.current.account !== account) session.current = { account, identity: {} };
  const identity = session.current.identity;
  const mounted = useRef(true);
  const entryRevision = useRef(0);
  const [snapshot, setSnapshot] = useState<EntrySnapshot>({ session: null, loading: true, skipOnboarding: false, campaignId: null });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (account === null) return;
    let cancelled = false;
    const load = async () => {
      const revision = entryRevision.current;
      try {
        const result = await client.query<{ myCampaignEntry: CampaignEntryState }>({
          query: MY_CAMPAIGN_ENTRY,
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
          context: { queryDeduplication: false },
        });
        if (cancelled || session.current.identity !== identity || revision !== entryRevision.current) return;
        if (result.errors?.length || ('error' in result && result.error) || !result.data?.myCampaignEntry) return;
        setSnapshot({ session: identity, loading: false, skipOnboarding: result.data.myCampaignEntry.skip_onboarding === true, campaignId: result.data.myCampaignEntry.campaign_id || null });
      } catch {
        // An unknown account preference must not open onboarding over a campaign.
        // Focus/online retry it; the entry page also has its explicit retry action.
      }
    };
    void load();
    window.addEventListener('focus', load);
    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', load);
      window.removeEventListener('online', load);
    };
  }, [account, client, identity]);

  const completeEntry = useCallback(async (campaignId: string) => {
    if (account === null) throw new Error('campaign_entry_unauthenticated');
    const current = () => mounted.current && session.current.identity === identity;
    try {
      const result = await client.mutate<{ completeMyCampaignEntry: CampaignEntryState }>({
        mutation: COMPLETE_MY_CAMPAIGN_ENTRY,
        variables: { campaignId },
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      });
      if (!current()) return false;
      const entry = result.data?.completeMyCampaignEntry;
      if (!entry?.account_id || entry.skip_onboarding !== true || entry.campaign_id !== campaignId) {
        throw new Error('campaign_entry_failed');
      }
      entryRevision.current += 1;
      setSnapshot({ session: identity, loading: false, skipOnboarding: true, campaignId });
      // Do not refetch shared observers: a new account could deduplicate onto
      // that request before it resolves. This refresh has no cache or observers.
      const fresh = await client.query<{ myData: { id: string } | null }>({
        query: MyDataDocument,
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
        context: { queryDeduplication: false },
      });
      if (!current()) return false;
      if (fresh.errors?.length || ('error' in fresh && fresh.error) || fresh.data?.myData?.id !== entry.account_id) {
        throw new Error('campaign_entry_failed');
      }
      return true;
    } catch (error) {
      if (!current()) return false;
      throw error;
    }
  }, [account, client, identity]);

  const value = useMemo(() => ({
    loading: isLoading || (account !== null && (snapshot.session !== identity || snapshot.loading)),
    skipOnboarding: account !== null && snapshot.session === identity && snapshot.skipOnboarding,
    campaignId: account !== null && snapshot.session === identity ? snapshot.campaignId : null,
    completeEntry,
  }), [isLoading, account, snapshot, identity, completeEntry]);

  // Keep the same child tree mounted across login/OAuth/account changes.
  return <CampaignEntryContext.Provider value={value}>{children}</CampaignEntryContext.Provider>;
}
